import React, {useEffect, useRef, useState} from "react";
import axios from "axios";
import {Form} from "react-bootstrap";
import {ReactSelectBootstrap} from "react-select-bootstrap";
import HeaderWithHelp from "./HeaderWithHelp.jsx";
import useDebouncedValue from "./useDebouncedValue.js";

/**
 * Filter „Farbton / Artikel“ (Rückmeldung André Bruland, 28.09.2026,
 * Paket 2): Suchauswahl über GET /vertrieb/daten/artikel. Gesucht wird
 * serverseitig ab zwei Zeichen nach Artikelnummer, Artikelname, Farbnummer
 * („#80“, „080“) oder Farbname. Gewählte Werte aus URL oder Browser-Speicher
 * bekommen ihre Beschriftung einmal beim Laden (numbers[]).
 */
export default function ArtikelFilter({value, onChange}) {
    const [eingabe, setEingabe] = useState("");
    const suche = useDebouncedValue(eingabe, 250);
    const [optionen, setOptionen] = useState([]);
    const [laedt, setLaedt] = useState(false);
    const [beschriftungen, setBeschriftungen] = useState({});
    const angefragtRef = useRef(new Set());

    useEffect(() => {
        const fehlend = (value || []).filter((nummer) => !beschriftungen[nummer] && !angefragtRef.current.has(nummer));
        if (!fehlend.length) {
            return;
        }
        fehlend.forEach((nummer) => angefragtRef.current.add(nummer));
        axios.get("/vertrieb/daten/artikel", {params: {numbers: fehlend}}).then(({data}) => {
            setBeschriftungen((alt) => {
                const neu = {...alt};
                (data?.options || []).forEach((o) => {
                    neu[o.value] = o.label;
                });
                return neu;
            });
        }).catch(() => {});
    }, [value, beschriftungen]);

    useEffect(() => {
        if (suche.trim().length < 2) {
            setOptionen([]);
            return undefined;
        }
        let aktiv = true;
        setLaedt(true);
        axios.get("/vertrieb/daten/artikel", {params: {q: suche}})
            .then(({data}) => {
                if (aktiv) {
                    setOptionen(data?.options || []);
                }
            })
            .catch(() => aktiv && setOptionen([]))
            .finally(() => aktiv && setLaedt(false));
        return () => {
            aktiv = false;
        };
    }, [suche]);

    const gewaehlt = (value || []).map((nummer) => ({value: nummer, label: beschriftungen[nummer] || nummer}));

    return (
        <div style={{minWidth: 240, flex: "1 1 240px", maxWidth: 360}}>
            <Form.Label className="small text-muted mb-1">
                <HeaderWithHelp help="Schränkt alle Zahlen dieser Seite auf einzelne Farbtöne oder Artikel ein. Suche nach Farbnummer (#080), Farbname, Artikelnummer oder Artikelname. Bis 2020 wurde auf die Übernummer gebucht, nicht auf den einzelnen Farbton - für die Jahre davor zeigt ein Farbton deshalb kaum Umsatz.">
                    Farbton / Artikel
                </HeaderWithHelp>
            </Form.Label>
            <ReactSelectBootstrap
                isMulti
                placeholder="Alle Artikel"
                inputValue={eingabe}
                onInputChange={(text, {action}) => {
                    if (action === "input-change") {
                        setEingabe(text);
                    } else if (action === "set-value" || action === "input-blur" || action === "menu-close") {
                        setEingabe("");
                    }
                }}
                filterOption={null}
                isLoading={laedt}
                noOptionsMessage={() => (eingabe.trim().length < 2 ? "Mindestens zwei Zeichen eingeben" : "Kein Artikel gefunden")}
                loadingMessage={() => "Wird gesucht…"}
                options={optionen}
                value={gewaehlt}
                onChange={(auswahl) => {
                    const liste = auswahl || [];
                    setBeschriftungen((alt) => {
                        const neu = {...alt};
                        liste.forEach((o) => {
                            neu[o.value] = o.label;
                        });
                        return neu;
                    });
                    onChange(liste.map((o) => o.value));
                }}
            />
        </div>
    );
}
